<script setup lang="ts">
useHead({
  title: 'Privacy Policy – SpreadIt',
  meta: [
    { name: 'description', content: 'SpreadIt Privacy Policy — how we collect, use, and protect your personal data.' },
  ],
});
const lastUpdated = 'April 20, 2025';
</script>

<template>
  <div class="max-w-3xl mx-auto px-6 py-14 text-gray-800 dark:text-gray-200">
    <!-- Header -->
    <div class="mb-10">
      <h1 class="text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-2">Privacy Policy</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400">Last updated: {{ lastUpdated }}</p>
    </div>

    <div class="prose prose-gray dark:prose-invert max-w-none space-y-8">

      <p>
        SpreadIt ("we", "us", or "our") operates the SpreadIt web application and related services (the "Service"). This Privacy
        Policy explains what information we collect, why we collect it, and how we use it. By using the Service you agree to the
        practices described here.
      </p>

      <!-- 1 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">1. Information We Collect</h2>

        <h3 class="text-base font-semibold mt-5 mb-1">a) Information you provide directly</h3>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed">
          <li>Account registration data: name and email address.</li>
          <li>Billing details processed on your behalf by our payment processor (Stripe). We do not store card numbers.</li>
          <li>Content you create: scripts, templates, credentials (stored encrypted), and execution context data.</li>
        </ul>

        <h3 class="text-base font-semibold mt-5 mb-1">b) Information collected automatically</h3>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed">
          <li>Log data: IP address, browser type and version, pages visited, timestamps, and referrer URLs.</li>
          <li>Session cookies and authentication tokens required to keep you signed in.</li>
          <li>Usage metrics: feature interactions used to improve the Service (never sold).</li>
        </ul>

        <h3 class="text-base font-semibold mt-5 mb-1">c) Information from Google Sign-In (OAuth 2.0)</h3>
        <p class="text-sm leading-relaxed">
          When you choose to sign in with Google, we request only the scopes necessary to authenticate you:
        </p>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed">
          <li><code>openid</code> — verifies your identity.</li>
          <li><code>email</code> — your primary Google account email address.</li>
          <li><code>profile</code> — your name and profile picture.</li>
        </ul>
        <p class="text-sm leading-relaxed mt-2">
          We do <strong>not</strong> request access to your Gmail, Google Drive, Calendar, or any other Google service data
          through this OAuth flow. If in the future we add optional Google Sheets integration,
          we will request only the minimum additional scopes required and will ask for explicit consent at that time.
        </p>
        <p class="text-sm leading-relaxed mt-2">
          SpreadIt's use and transfer of information received from Google APIs adheres to the
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary-600 dark:text-primary-400 underline"
          >Google API Services User Data Policy</a>,
          including the Limited Use requirements.
        </p>
      </section>

      <!-- 2 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">2. How We Use Your Information</h2>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed">
          <li>To create and manage your account and authenticate you securely.</li>
          <li>To deliver, operate, and improve the Service.</li>
          <li>To send transactional emails (account confirmation, password reset, billing receipts).</li>
          <li>To respond to support requests.</li>
          <li>To detect, investigate, and prevent fraud or abuse.</li>
          <li>To comply with legal obligations.</li>
        </ul>
        <p class="text-sm leading-relaxed mt-3">
          We will not use your Google user data for any purpose other than providing or improving user-facing features of the Service.
          We do not use Google user data for advertising, profiling, or selling to third parties.
        </p>
      </section>

      <!-- 3 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">3. Data Sharing and Disclosure</h2>
        <p class="text-sm leading-relaxed">
          We do not sell, rent, or trade your personal information. We may share data with:
        </p>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed mt-2">
          <li>
            <strong>Service providers</strong> who act on our behalf (e.g., cloud hosting, payment processing, email delivery).
            These parties are contractually bound to protect your data and may not use it for their own purposes.
          </li>
          <li>
            <strong>Identity provider</strong>: We use Zitadel to manage authentication. When you sign in, Zitadel processes
            your authentication credentials. See
            <a href="https://zitadel.com/privacy" target="_blank" rel="noopener noreferrer"
               class="text-primary-600 dark:text-primary-400 underline">Zitadel's Privacy Policy</a>.
          </li>
          <li>
            <strong>Legal authorities</strong>: if required by law, court order, or to protect the rights and safety of SpreadIt
            or others.
          </li>
          <li>
            <strong>Business transfers</strong>: in connection with a merger, acquisition, or sale of assets, with notice to you.
          </li>
        </ul>
      </section>

      <!-- 4 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">4. Data Storage and Security</h2>
        <p class="text-sm leading-relaxed">
          Your data is stored on servers in the United States. API credentials and secrets you store in the Service are encrypted
          at rest using AES-256-GCM and are never transmitted to the browser in plaintext.
        </p>
        <p class="text-sm leading-relaxed mt-2">
          We apply industry-standard technical and organizational measures to protect your information, including TLS in transit,
          encrypted storage, and access controls. No method of transmission over the internet is 100% secure; we cannot guarantee
          absolute security but we will notify you of any breach that affects your data as required by applicable law.
        </p>
      </section>

      <!-- 5 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">5. Data Retention</h2>
        <p class="text-sm leading-relaxed">
          We retain your personal data for as long as your account is active or as needed to provide the Service. If you close
          your account, we will delete or anonymize your personal data within 30 days, except where we are required by law to
          retain it longer (e.g., tax records).
        </p>
      </section>

      <!-- 6 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">6. Cookies</h2>
        <p class="text-sm leading-relaxed">
          We use strictly necessary cookies to maintain your authenticated session. We do not use tracking cookies or third-party
          advertising cookies. You may configure your browser to reject cookies, but this will prevent you from signing in.
        </p>
      </section>

      <!-- 7 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">7. Your Rights</h2>
        <p class="text-sm leading-relaxed">Depending on your location you may have the right to:</p>
        <ul class="list-disc pl-6 space-y-1 text-sm leading-relaxed mt-2">
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate personal data.</li>
          <li>Request deletion of your personal data (see the "Delete My Account" page).</li>
          <li>Object to or restrict certain processing.</li>
          <li>Data portability — receive your data in a structured, machine-readable format.</li>
          <li>Withdraw consent at any time (where processing is based on consent).</li>
        </ul>
        <p class="text-sm leading-relaxed mt-2">
          To exercise these rights, contact us at <a href="mailto:privacy@spreadit.app" class="text-primary-600 dark:text-primary-400 underline">privacy@spreadit.app</a>.
          We will respond within 30 days.
        </p>
      </section>

      <!-- 8 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">8. Children's Privacy</h2>
        <p class="text-sm leading-relaxed">
          The Service is not directed to children under 13. We do not knowingly collect personal information from children.
          If you believe a child has provided us with personal data, please contact us and we will delete it promptly.
        </p>
      </section>

      <!-- 9 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">9. Changes to This Policy</h2>
        <p class="text-sm leading-relaxed">
          We may update this Privacy Policy from time to time. When we make material changes, we will notify you by email or by
          posting a notice on the Service before the changes take effect. Continued use of the Service after the effective date
          constitutes acceptance of the updated policy.
        </p>
      </section>

      <!-- 10 -->
      <section>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mt-8 mb-3">10. Contact Us</h2>
        <p class="text-sm leading-relaxed">
          If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
        </p>
        <address class="not-italic mt-3 text-sm leading-relaxed">
          <strong>SpreadIt</strong><br />
          Email: <a href="mailto:privacy@spreadit.app" class="text-primary-600 dark:text-primary-400 underline">privacy@spreadit.app</a>
        </address>
      </section>
    </div>

    <!-- Footer nav -->
    <div class="mt-12 pt-6 border-t border-gray-200 dark:border-gray-800 flex gap-4 text-sm text-gray-500">
      <NuxtLink to="/terms" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Terms of Service</NuxtLink>
      <NuxtLink to="/" class="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">Back to Home</NuxtLink>
    </div>
  </div>
</template>
